<script setup>
// Editor de query nomeada (Fase 6, spec §4.6): nome, key, quando roda, SQL parametrizado (placeholders `:nome`) e
// os parâmetros declarados (nome, tipo, obrigatório, amarração no contato). O SQL é revisado por um admin e
// validado no servidor contra a allowlist da fonte — a IA nunca escreve SQL.
import { computed, reactive, ref } from 'vue';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { parseAPIErrorResponse } from 'dashboard/store/utils/api';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import { Checkbox } from 'dashboard/components-next/polaris/ui/checkbox';
import { Textarea } from 'dashboard/components-next/polaris/ui/textarea';
import { Delete16, Add16 } from '@carbon/icons-vue';
import PolarisDataSourceAPI from 'dashboard/api/polaris/dataSources';

const props = defineProps({
  sourceId: { type: Number, required: true },
  query: { type: Object, default: null },
});

const emit = defineEmits(['close', 'saved']);
const { t } = useI18n();

const dialogRef = ref(null);
const saving = ref(false);

const i18nKey = 'POLARIS.DATA_SOURCES.QUERY_FORM';

const RUN_ON_OPTIONS = [
  { value: 'on_demand', label: t(`${i18nKey}.RUN_ON.ON_DEMAND`) },
  {
    value: 'conversation_start',
    label: t(`${i18nKey}.RUN_ON.CONVERSATION_START`),
  },
];
const PARAM_TYPE_OPTIONS = ['string', 'integer', 'number', 'boolean'].map(
  value => ({ value, label: value })
);
const BIND_OPTIONS = [
  { value: '', label: t(`${i18nKey}.PARAM_BIND.NONE`) },
  { value: 'contact_email', label: t(`${i18nKey}.PARAM_BIND.CONTACT_EMAIL`) },
  { value: 'contact_phone', label: t(`${i18nKey}.PARAM_BIND.CONTACT_PHONE`) },
];

const blankParam = () => ({
  name: '',
  type: 'string',
  required: true,
  bind: '',
});

const state = reactive({
  name: props.query?.name || '',
  key: props.query?.key || '',
  runOn: props.query?.run_on || 'on_demand',
  sql: props.query?.sql || '',
  assistantId: props.query?.assistant_id || null,
  params: props.query?.params?.length
    ? props.query.params.map(param => ({
        name: param.name,
        type: param.type || 'string',
        required: Boolean(param.required),
        bind: param.bind || '',
      }))
    : [],
});

const assistants = useMapGetter('polarisAssistants/getRecords');
const assistantOptions = computed(() => [
  { value: null, label: t(`${i18nKey}.ASSISTANT.ALL`) },
  ...assistants.value.map(assistant => ({
    value: assistant.id,
    label: assistant.name,
  })),
]);

const formValid = computed(
  () =>
    state.name.trim() &&
    /^[a-z][a-z0-9_]*$/.test(state.key) &&
    state.sql.trim() &&
    state.params.every(param => /^[a-z][a-z0-9_]*$/.test(param.name))
);

const addParam = () => state.params.push(blankParam());
const removeParam = index => state.params.splice(index, 1);

const buildPayload = () => ({
  name: state.name,
  key: state.key,
  run_on: state.runOn,
  sql: state.sql,
  assistant_id: state.assistantId,
  params: state.params.map(param => ({
    name: param.name,
    type: param.type,
    required: param.required,
    ...(param.bind ? { bind: param.bind } : {}),
  })),
});

const handleSubmit = async () => {
  if (!formValid.value) return;
  saving.value = true;
  try {
    if (props.query?.id) {
      await PolarisDataSourceAPI.updateQuery(
        props.sourceId,
        props.query.id,
        buildPayload()
      );
    } else {
      await PolarisDataSourceAPI.createQuery(props.sourceId, buildPayload());
    }
    useAlert(t('POLARIS.DATA_SOURCES.QUERIES.SAVE_SUCCESS'));
    emit('saved');
    dialogRef.value.close();
  } catch (error) {
    useAlert(
      parseAPIErrorResponse(error) ||
        t('POLARIS.DATA_SOURCES.QUERIES.SAVE_ERROR')
    );
  } finally {
    saving.value = false;
  }
};

const handleClose = () => emit('close');
const handleCancel = () => dialogRef.value.close();

defineExpose({ dialogRef });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="
      query?.id ? $t(`${i18nKey}.TITLE_EDIT`) : $t(`${i18nKey}.TITLE_NEW`)
    "
    :description="$t(`${i18nKey}.DESCRIPTION`)"
    :show-cancel-button="false"
    :show-confirm-button="false"
    @close="handleClose"
  >
    <form class="flex flex-col gap-4" @submit.prevent="handleSubmit">
      <div class="grid grid-cols-2 gap-3">
        <Input
          v-model="state.name"
          :label="t(`${i18nKey}.NAME.LABEL`)"
          :placeholder="t(`${i18nKey}.NAME.PLACEHOLDER`)"
        />
        <Input
          v-model="state.key"
          :label="t(`${i18nKey}.KEY.LABEL`)"
          :placeholder="t(`${i18nKey}.KEY.PLACEHOLDER`)"
          :message="t(`${i18nKey}.KEY.HELP`)"
          message-type="info"
        />
      </div>
      <div class="grid grid-cols-2 gap-3">
        <div class="flex flex-col gap-1">
          <label class="mb-0.5 text-sm font-medium text-n-slate-12">
            {{ t(`${i18nKey}.RUN_ON.LABEL`) }}
          </label>
          <ComboBox v-model="state.runOn" :options="RUN_ON_OPTIONS" />
        </div>
        <div class="flex flex-col gap-1">
          <label class="mb-0.5 text-sm font-medium text-n-slate-12">
            {{ t(`${i18nKey}.ASSISTANT.LABEL`) }}
          </label>
          <ComboBox v-model="state.assistantId" :options="assistantOptions" />
        </div>
      </div>
      <div class="flex flex-col gap-1">
        <label class="mb-0.5 text-sm font-medium text-n-slate-12">
          {{ t(`${i18nKey}.SQL.LABEL`) }}
        </label>
        <Textarea
          v-model="state.sql"
          rows="4"
          class="font-mono text-xs"
          :placeholder="t(`${i18nKey}.SQL.PLACEHOLDER`)"
        />
        <p class="text-xs text-n-slate-11">
          {{ t(`${i18nKey}.SQL.HELP`) }}
        </p>
      </div>

      <div class="flex flex-col gap-2">
        <div class="flex items-center justify-between">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t(`${i18nKey}.PARAMS.LABEL`) }}
          </label>
          <Button type="button" size="small" variant="ghost" @click="addParam">
            <Add16 />
            {{ t(`${i18nKey}.PARAMS.ADD`) }}
          </Button>
        </div>
        <div
          v-for="(param, index) in state.params"
          :key="index"
          class="grid grid-cols-[1fr_96px_auto_1fr_auto] items-center gap-2"
        >
          <Input
            v-model="param.name"
            :placeholder="t(`${i18nKey}.PARAMS.NAME_PLACEHOLDER`)"
          />
          <ComboBox v-model="param.type" :options="PARAM_TYPE_OPTIONS" />
          <label class="flex items-center gap-1 text-xs text-n-slate-12">
            <Checkbox v-model="param.required" />
            {{ t(`${i18nKey}.PARAM_REQUIRED`) }}
          </label>
          <ComboBox v-model="param.bind" :options="BIND_OPTIONS" />
          <Button
            type="button"
            size="small"
            variant="ghost"
            @click="removeParam(index)"
          >
            <Delete16 />
          </Button>
        </div>
      </div>

      <div class="flex justify-end gap-2">
        <Button type="button" variant="ghost" @click="handleCancel">
          {{ t('POLARIS.DATA_SOURCES.FORM.CANCEL') }}
        </Button>
        <Button type="submit" :is-loading="saving" :disabled="!formValid">
          {{ t('POLARIS.DATA_SOURCES.FORM.SAVE') }}
        </Button>
      </div>
    </form>
    <template #footer />
  </Dialog>
</template>
